import { api } from "../api";
import type { CatalogMethod } from "../catalog/catalog-required-fields";

export type MetadataSource = "tenant" | "platform" | "external";

export interface AutofillResponse {
  biblio: {
    title: string;
    subtitle?: string;
    authors: string[];
    editors?: string[];
    edition?: string;
    isbn?: string;
    issn?: string;
    doi?: string;
    publisher?: string;
    placeOfPublication?: string;
    publishedDate?: string;
    pages?: number;
    illustrations?: string;
    size?: string;
    seriesInformation?: string;
    subjects: string[];
    description?: string;
    coverUrl?: string;
    lcClassification?: string;
    callNumber?: string;
    bindingType?: string; // "hbk" (hardback) or "pbk" (paperback)
    source: string[];
  };
  suggestedItem: {
    itemTypeId?: string;
    locationId?: string;
    collectionId?: string;
    branchId?: string;
    conditionCode?: string;
    statusCode: string;
    acquisitionMode?: string;
  };
  barcodesPreview: string[];
  accessionStart: number;
  existingBiblioId?: string;
  existingCopyCount?: number;
  confidence: number;
  metadataSource?: MetadataSource;
  canRefresh?: boolean;
}

export interface CreateCatalogRequest {
  biblio: {
    title: string;
    subtitle?: string;
    authors: string[];
    editors?: string[]; // For journals
    edition?: string;
    isbn?: string;
    issn?: string;
    eIssn?: string;
    issnL?: string;
    doi?: string;
    publisher?: string;
    placeOfPublication?: string;
    publishedDate?: string;
    pages?: number;
    illustrations?: string;
    size?: string;
    seriesInformation?: string;
    subjects?: string[];
    description?: string;
    coverUrl?: string;
    websiteUrl?: string;
    lcClassification?: string;
    callNumber?: string;
    bindingType?: string; // "hbk" (hardback) or "pbk" (paperback)
  };
  copies: number;
  itemDefaults: {
    itemTypeId?: string;
    locationId?: string;
    collectionId?: string;
    branchId?: string;
    conditionCode?: string;
    statusCode?: string;
    acquisitionMode?: string;
    accessionNumbers?: number[]; // Optional manual accession numbers (legacy support)
  };
  // Optional per-copy item details for individual copy configuration
  itemCopies?: ItemCopyDetails[];
  /** Catalog creation method for validation rules (MANUAL, ISBN, JOURNAL, etc.). */
  catalogMethod?: CatalogMethod;
  journal?: {
    volumeNumber: string;
    volumePublicationYear?: string;
    issueNumberFrom?: number;
    issueNumberTo?: number;
    issuePublicationYear?: string;
    issuePublicationDate?: string;
    volumeOnly?: boolean;
  };
  journalHoldings?: Array<{
    volumeNumber: string;
    volumePublicationYear?: string;
    issueNumberFrom?: number;
    issueNumberTo?: number;
    issuePublicationYear?: string;
    issuePublicationDate?: string;
    volumeOnly?: boolean;
    copies: number;
    itemCopies?: ItemCopyDetails[];
  }>;
  /** When true, only title is required (draft save). */
  saveAsDraft?: boolean;
  workflowIntent?: "publish" | "submit-for-review";
}

export interface CreateCatalogResponse {
  success: boolean;
  biblioId: string;
  items: Array<{
    id: string;
    barcode: string;
    accessionNumber: number;
    copyNumber: number;
  }>;
}

export interface BarcodePattern {
  pattern: string;
  lastSequence: number;
}

export type ApprovalWorkflow = "disabled" | "optional" | "required";

export type PublicationStatus = "DRAFT" | "PENDING_REVIEW" | "RETURNED" | "REJECTED" | "PUBLISHED";

export interface CatalogSettings {
  accessionNumberMode: "AUTO" | "MANUAL";
  startingAccessionNumber?: number;
  approvalWorkflow?: ApprovalWorkflow;
}

export interface CatalogWorkflowRecord {
  id: string;
  title: string;
  authors: string[];
  isbn?: string | null;
  issn?: string | null;
  publicationStatus: PublicationStatus;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string | null;
  reviewedAt?: string | null;
  reviewNotes?: string | null;
  catalogMethod?: CatalogMethod;
  createdBy?: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null;
}

export interface CatalogDraftsResponse {
  data: CatalogWorkflowRecord[];
  total: number;
}

export interface JournalCatalogDraft {
  id: string;
  publicationStatus: Extract<PublicationStatus, "DRAFT" | "RETURNED">;
  payload: CreateCatalogRequest & { catalogMethod: "JOURNAL" };
}

export interface CatalogReviewQueueResponse {
  data: CatalogWorkflowRecord[];
  total: number;
}

// Per-copy item details for individual copy configuration
// Note: collectionId is not included as collections are logical groupings
// that don't typically vary per physical copy of the same book
export interface ItemCopyDetails {
  accessionNumber?: number;
  conditionCode?: string;
  statusCode?: string;
  locationId?: string;
  branchId?: string; // Which branch this physical copy belongs to
  acquisitionMode?: string;
}

export interface CatalogItemCopy {
  id: string;
  barcode: string;
  copyNumber: number;
  accessionNumber: number;
  statusCode: string;
  withdrawnAt?: string | null;
  withdrawnBy?: string | null;
  withdrawalReason?: string | null;
  branchId?: string | null;
  locationId?: string | null;
  collectionId?: string | null;
  itemTypeId?: string | null;
  conditionCode?: string | null;
  acquisitionMode?: string | null;
  autoRenewalDisabled?: boolean;
  branch?: { id: string; name: string };
  location?: { id: string; name: string };
  collection?: { id: string; name: string };
  itemType?: { id: string; name: string };
}

export interface DigitalResource {
  id: string;
  tenantId: string;
  title: string;
  description?: string;
  authors: string[];
  type: string;
  fileUrl: string;
  /** True when fileUrl points at an external website (not a storage object). */
  isExternalUrl?: boolean;
  fileSize?: number;
  mimeType?: string;
  duration?: number;
  thumbnailUrl?: string;
  publisher?: string;
  publishedDate?: string;
  subjects: string[];
  tags: string[];
  lcClassification?: string;
  callNumber?: string;
  collectionId?: string;
  biblioId?: string;
  isPublic: boolean;
  downloadCount: number;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  metadata?: Record<string, unknown>;
  biblio?: any;
  collection?: any;
}

export interface CreateDigitalResourceRequest {
  title: string;
  description?: string;
  authors?: string[];
  type:
    | "PDF"
    | "VIDEO"
    | "AUDIO"
    | "EBOOK"
    | "JOURNAL"
    | "RESEARCH_PAPER"
    | "INSTITUTIONAL_CONTENT"
    | "WEBSITE";
  publisher?: string;
  publishedDate?: string;
  subjects?: string[];
  tags?: string[];
  lcClassification?: string;
  callNumber?: string;
  collectionId?: string;
  biblioId?: string;
  isPublic?: boolean;
  allowDownload?: boolean;
  /** External website URL when the resource is not a downloadable file. */
  websiteUrl?: string;
}

export interface UnifiedSearchResult {
  type: "physical" | "digital";
  id: string;
  title: string;
  authors: string[];
  pricingRuleCount?: number;
  isPublic?: boolean;
  fileType?: string;
  [key: string]: any;
}

export interface CatalogPageFilters {
  collections: Array<{ id: string; name: string; isActive: boolean }>;
  itemTypes: Array<{ id: string; name: string; code: string; isActive: boolean }>;
  branches: Array<{ id: string; name: string; code: string; isMain: boolean }>;
}

export interface UnifiedSearchResponse {
  data: UnifiedSearchResult[];
  total: number;
  page: number;
  pageSize: number;
  nextCursor?: string;
  stats?: { totalPhysical: number; totalDigital: number };
  filters?: CatalogPageFilters;
}

export interface ProcessingQueueRow {
  biblioId: string;
  title: string;
  authors: string[];
  isbn: string | null;
  processingItemCount: number;
  totalItemCount: number;
  oldestProcessingAt: string;
}

export interface ProcessingQueueResponse {
  data: ProcessingQueueRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface JournalHoldingsTreeItem {
  id: string;
  barcode: string;
  accessionNumber: number;
  copyNumber: number;
  statusCode: string;
  location?: { id: string; name: string } | null;
}

export interface JournalHoldingsTreeIssue {
  id: string;
  issueLabel: string;
  isDefault: boolean;
  publicationYear: string | null;
  publicationDate: string | null;
  copyCount: number;
  items: JournalHoldingsTreeItem[];
}

export interface JournalHoldingsTreeVolume {
  id: string;
  volumeNumber: string;
  publicationYear: string | null;
  issueCount: number;
  copyCount: number;
  issues: JournalHoldingsTreeIssue[];
}

export interface JournalHoldingsTree {
  biblioId: string;
  volumes: JournalHoldingsTreeVolume[];
}

export interface ReceiveJournalIssueRequest {
  volumeNumber: string;
  volumePublicationYear?: string;
  issueNumberFrom?: number;
  issueNumberTo?: number;
  issuePublicationYear?: string;
  issuePublicationDate?: string;
  volumeOnly?: boolean;
  copies: number;
  itemDefaults: CreateCatalogRequest["itemDefaults"];
  itemCopies?: ItemCopyDetails[];
}

export interface ReceiveJournalIssueResponse {
  volumeId: string;
  issueId: string;
  issueLabel: string;
  createdCopies: number;
}

export interface CatalogMatchResult {
  matchType: "none" | "isbn" | "issn";
  biblioId: string | null;
  biblio: {
    title: string;
    authors: string[];
    isbn?: string | null;
    issn?: string | null;
    callNumber?: string | null;
    lcClassification?: string | null;
    coverUrl?: string | null;
  } | null;
  existingCopyCount: number;
  suggestedItemDefaults: AutofillResponse["suggestedItem"] | null;
  hasStructuredHoldings?: boolean;
  volumeCount?: number;
  issueCount?: number;
}

export const catalogApi = {
  /**
   * Autofill metadata and suggestions for an ISBN
   */
  autofillMetadata: async (
    isbn: string,
    options?: { refresh?: boolean }
  ): Promise<AutofillResponse> => {
    const response = await api.post(
      "/catalog/autofill",
      {
        isbn,
        refresh: options?.refresh === true,
      },
      {
        timeout: 45_000,
      }
    );
    return response.data;
  },

  /**
   * Match ISBN/ISSN against existing bibliographic records
   */
  matchCatalog: async (params: { isbn?: string; issn?: string }): Promise<CatalogMatchResult> => {
    const response = await api.get<CatalogMatchResult>("/catalog/match", { params });
    return response.data;
  },

  /**
   * Create bibliographic record with items
   */
  createCatalogEntry: async (data: CreateCatalogRequest): Promise<CreateCatalogResponse> => {
    const response = await api.post("/catalog/create", data);
    return response.data;
  },

  /**
   * Update an individual item (copy) - branch, location, condition, status, etc.
   */
  updateItem: async (
    itemId: string,
    data: {
      branchId?: string | null;
      locationId?: string | null;
      collectionId?: string | null;
      itemTypeId?: string | null;
      conditionCode?: string | null;
      statusCode?: string;
      /** When true, automated renewal skips this copy. */
      autoRenewalDisabled?: boolean;
    }
  ): Promise<any> => {
    const response = await api.patch(`/catalog/items/${itemId}`, data);
    return response.data;
  },

  /**
   * Add more physical copies to an existing bibliographic record
   */
  addCopiesToBiblio: async (
    biblioId: string,
    data: {
      copies: number;
      itemDefaults: CreateCatalogRequest["itemDefaults"];
      itemCopies?: ItemCopyDetails[];
    }
  ): Promise<CreateCatalogResponse> => {
    const response = await api.post(`/catalog/biblio/${biblioId}/add-copies`, data);
    return response.data;
  },

  getJournalHoldings: async (biblioId: string): Promise<JournalHoldingsTree> => {
    const response = await api.get<JournalHoldingsTree>(
      `/catalog/biblio/${biblioId}/journal-holdings`
    );
    return response.data;
  },

  getJournalDraft: async (biblioId: string): Promise<JournalCatalogDraft> => {
    const response = await api.get<JournalCatalogDraft>(`/catalog/journal-drafts/${biblioId}`);
    return response.data;
  },

  updateJournalDraft: async (
    biblioId: string,
    data: CreateCatalogRequest
  ): Promise<JournalCatalogDraft> => {
    const response = await api.put<JournalCatalogDraft>(
      `/catalog/journal-drafts/${biblioId}`,
      data
    );
    return response.data;
  },

  receiveIssue: async (
    biblioId: string,
    data: ReceiveJournalIssueRequest
  ): Promise<ReceiveJournalIssueResponse> => {
    const response = await api.post<ReceiveJournalIssueResponse>(
      `/catalog/biblio/${biblioId}/receive-issue`,
      data
    );
    return response.data;
  },

  /**
   * Get barcode pattern configuration
   */
  getBarcodePattern: async (): Promise<BarcodePattern> => {
    const response = await api.get("/catalog/barcode-pattern");
    return response.data;
  },

  /**
   * Update barcode pattern configuration
   */
  updateBarcodePattern: async (pattern: string): Promise<void> => {
    await api.put("/catalog/barcode-pattern", { pattern });
  },

  /**
   * Get catalog settings including accession number mode
   */
  getCatalogSettings: async (): Promise<CatalogSettings> => {
    const response = await api.get("/catalog/settings");
    return response.data;
  },

  /**
   * Update catalog settings
   */
  updateCatalogSettings: async (
    settings: Partial<CatalogSettings>
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.put("/catalog/settings", settings);
    return response.data;
  },

  /**
   * Upload and create digital resource (file and/or website URL)
   */
  uploadDigitalResource: async (
    file: File | null,
    data: CreateDigitalResourceRequest
  ): Promise<DigitalResource> => {
    const formData = new FormData();
    if (file) formData.append("file", file);
    formData.append("title", data.title);
    if (data.description) formData.append("description", data.description);
    if (data.authors) formData.append("authors", JSON.stringify(data.authors));
    formData.append("type", data.type);
    if (data.publisher) formData.append("publisher", data.publisher);
    if (data.publishedDate) formData.append("publishedDate", data.publishedDate);
    if (data.subjects) formData.append("subjects", JSON.stringify(data.subjects));
    if (data.tags) formData.append("tags", JSON.stringify(data.tags));
    if (data.lcClassification) formData.append("lcClassification", data.lcClassification);
    if (data.callNumber) formData.append("callNumber", data.callNumber);
    if (data.collectionId) formData.append("collectionId", data.collectionId);
    if (data.biblioId) formData.append("biblioId", data.biblioId);
    if (data.isPublic !== undefined) formData.append("isPublic", String(data.isPublic));
    if (data.allowDownload !== undefined)
      formData.append("allowDownload", String(data.allowDownload));
    if (data.websiteUrl) formData.append("websiteUrl", data.websiteUrl);

    const response = await api.post("/catalog/digital-resources/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  /**
   * Create digital resource (without file upload)
   */
  createDigitalResource: async (
    data: CreateDigitalResourceRequest & { fileUrl: string }
  ): Promise<DigitalResource> => {
    const response = await api.post("/catalog/digital-resources", data);
    return response.data;
  },

  /**
   * List digital resources
   */
  listDigitalResources: async (
    page?: number,
    pageSize?: number,
    filters?: any
  ): Promise<{ data: DigitalResource[]; total: number; page: number; pageSize: number }> => {
    const response = await api.get("/catalog/digital-resources", {
      params: { page, pageSize, ...filters },
    });
    return response.data;
  },

  /**
   * Get digital resource by ID
   */
  getDigitalResource: async (id: string): Promise<DigitalResource> => {
    const response = await api.get(`/catalog/digital-resources/${id}`);
    return response.data;
  },

  /**
   * Get public digital resource by ID (no authentication required)
   */
  getPublicDigitalResource: async (id: string, tenantSlug?: string): Promise<DigitalResource> => {
    // For localhost/development, pass tenant slug as query param
    const params: Record<string, string> = {};
    if (tenantSlug) {
      params.tenantSlug = tenantSlug;
    }
    const response = await api.get(`/catalog/public/digital-resources/${id}`, { params });
    return response.data;
  },

  /**
   * Update digital resource
   */
  updateDigitalResource: async (
    id: string,
    data: Partial<CreateDigitalResourceRequest> & { fileUrl?: string }
  ): Promise<DigitalResource> => {
    const response = await api.put(`/catalog/digital-resources/${id}`, data);
    return response.data;
  },

  /**
   * Delete digital resource
   */
  deleteDigitalResource: async (id: string): Promise<void> => {
    await api.delete(`/catalog/digital-resources/${id}`);
  },

  /**
   * Unified search across all catalog resources.
   * includeStats/includeFilters: bundle stats and filter options in one call (optimized for catalog page initial load).
   * cursor: pass for next page (cursor-based pagination).
   * signal: AbortSignal to cancel request (avoids duplicate calls in React Strict Mode).
   */
  unifiedSearch: async (
    query: string,
    type?: "physical" | "digital" | "all",
    page?: number,
    pageSize?: number,
    filters?: any,
    options?: {
      includeStats?: boolean;
      includeFilters?: boolean;
      cursor?: string;
      signal?: AbortSignal;
    }
  ): Promise<UnifiedSearchResponse> => {
    const params: Record<string, any> = { q: query, type, page, pageSize, ...filters };
    if (options?.includeStats) params.includeStats = "true";
    if (options?.includeFilters) params.includeFilters = "true";
    if (options?.cursor) params.cursor = options.cursor;
    const response = await api.get("/catalog/search", { params, signal: options?.signal });
    return response.data;
  },

  /**
   * Get all catalog resources (physical and digital)
   */
  getAllResources: async (
    page?: number,
    pageSize?: number,
    filters?: any
  ): Promise<UnifiedSearchResponse> => {
    const response = await api.get("/catalog/all", {
      params: { page, pageSize, ...filters },
    });
    return response.data;
  },

  /**
   * Get book (Biblio) by ID
   */
  getBook: async (id: string): Promise<any> => {
    const response = await api.get(`/catalog/books/${id}`);
    return response.data;
  },

  /**
   * Update book (Biblio)
   */
  updateBook: async (id: string, data: any): Promise<any> => {
    const response = await api.put(`/catalog/books/${id}`, data);
    return response.data;
  },

  /**
   * Delete legacy `Book` record (old catalog model only).
   */
  deleteBook: async (id: string): Promise<void> => {
    await api.delete(`/catalog/books/${id}`);
  },

  /**
   * Delete a bibliographic record (Biblio) and its item copies. Use this for cataloged titles.
   */
  deleteBiblio: async (id: string): Promise<void> => {
    await api.delete(`/catalog/biblio/${id}`);
  },

  deleteDraft: async (id: string): Promise<void> => {
    await api.delete(`/catalog/drafts/${id}`);
  },

  /**
   * Biblios with at least one copy in PROCESSING (acquisition cataloging handoff).
   */
  getProcessingQueue: async (params?: {
    page?: number;
    pageSize?: number;
    acquisitionMode?: string;
  }): Promise<ProcessingQueueResponse> => {
    const response = await api.get("/catalog/processing-queue", { params });
    return response.data;
  },

  /**
   * Get Biblio by ID with items
   */
  getBiblio: async (id: string): Promise<any> => {
    const response = await api.get(`/catalog/biblio/${id}`);
    return response.data;
  },

  /**
   * Update Biblio (physical book)
   */
  updateBiblio: async (id: string, data: any): Promise<any> => {
    const response = await api.put(`/catalog/biblio/${id}`, data);
    return response.data;
  },

  /**
   * Withdraw a copy from circulation (sets status WITHDRAWN)
   */
  withdrawCopy: async (itemId: string, reason: string): Promise<CatalogItemCopy> => {
    const response = await api.post(`/catalog/items/${itemId}/withdraw`, { reason });
    return response.data;
  },

  /**
   * Reactivate a withdrawn copy (sets status AVAILABLE)
   */
  reactivateCopy: async (itemId: string): Promise<CatalogItemCopy> => {
    const response = await api.post(`/catalog/items/${itemId}/reactivate`);
    return response.data;
  },

  /**
   * Hard-delete a copy when backend guards pass (no circulation history)
   */
  deleteCopy: async (itemId: string): Promise<{ success: boolean }> => {
    const response = await api.delete(`/catalog/items/${itemId}`);
    return response.data;
  },

  /**
   * List draft and returned catalog records for the current user (or all when permitted)
   */
  listDrafts: async (): Promise<CatalogDraftsResponse> => {
    const response = await api.get("/catalog/drafts");
    const payload = response.data;
    if (Array.isArray(payload)) {
      return { data: payload, total: payload.length };
    }
    return {
      data: payload?.data ?? [],
      total: payload?.total ?? payload?.data?.length ?? 0,
    };
  },

  /**
   * List records awaiting catalog review
   */
  listReviewQueue: async (): Promise<CatalogReviewQueueResponse> => {
    const response = await api.get("/catalog/review-queue");
    const payload = response.data;
    if (Array.isArray(payload)) {
      return { data: payload, total: payload.length };
    }
    return {
      data: payload?.data ?? [],
      total: payload?.total ?? payload?.data?.length ?? 0,
    };
  },

  /**
   * Submit a draft or returned record for review (or publish when workflow allows)
   */
  submitBiblio: async (
    biblioId: string,
    data: CreateCatalogRequest
  ): Promise<{ success: boolean; publicationStatus: PublicationStatus }> => {
    const response = await api.post(`/catalog/biblio/${biblioId}/submit`, data);
    return response.data;
  },

  /**
   * Approve and publish a record in the review queue
   */
  approveBiblio: async (
    biblioId: string
  ): Promise<{ success: boolean; publicationStatus: PublicationStatus }> => {
    const response = await api.post(`/catalog/biblio/${biblioId}/approve`);
    return response.data;
  },

  /**
   * Return a record to the creator with required correction notes
   */
  returnBiblio: async (
    biblioId: string,
    notes: string
  ): Promise<{ success: boolean; publicationStatus: PublicationStatus }> => {
    const response = await api.post(`/catalog/biblio/${biblioId}/return`, { notes });
    return response.data;
  },

  /**
   * Reject a record with a required reason (terminal, view-only for creator)
   */
  rejectBiblio: async (
    biblioId: string,
    reason: string
  ): Promise<{ success: boolean; publicationStatus: PublicationStatus }> => {
    const response = await api.post(`/catalog/biblio/${biblioId}/reject`, { reason });
    return response.data;
  },
};
